import { useState, useMemo } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';
import { useBrazilCities } from '../../hooks/iframe/useBrazilCities';

const CitySelect = ({ value, onChange, placeholder, filterByState = null }) => {
  const { cities, loading } = useBrazilCities();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filtrar cidades por estado (se fornecido) e termo de busca
  const filteredCities = useMemo(() => {
    let filtered = cities;

    // Filtrar por estado se fornecido
    if (filterByState) {
      filtered = filtered.filter(city => city.state === filterByState);
    }

    // Filtrar por termo de busca
    if (searchTerm) {
      filtered = filtered.filter(city =>
        city.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Limitar a 50 resultados
    return filtered.slice(0, 50);
  }, [cities, searchTerm, filterByState]);

  const handleSelect = (city) => {
    onChange(city.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        <span className="text-sm text-gray-500">Carregando cidades...</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Campo de seleção */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 cursor-pointer bg-white"
      >
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gray-400" />
          <span className={value ? 'text-gray-900' : 'text-gray-400'}>
            {value || placeholder || 'Selecione a cidade'}
          </span>
        </div>
      </div>

      {/* Dropdown com busca */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg">
          {/* Campo de busca */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar cidade..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                autoFocus
              />
            </div>
          </div>

          {/* Lista de cidades */}
          <div className="max-h-64 overflow-y-auto">
            {filteredCities.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                Nenhuma cidade encontrada
              </div>
            ) : (
              filteredCities.map((city) => (
                <div
                  key={city.value}
                  onClick={() => handleSelect(city)}
                  className="px-4 py-2 hover:bg-indigo-50 cursor-pointer transition-colors"
                >
                  <span className="text-sm text-gray-900">{city.label}</span>
                </div>
              ))
            )}
          </div>

          {/* Footer com contador */}
          {filteredCities.length > 0 && (
            <div className="px-4 py-2 border-t text-xs text-gray-500 text-center">
              {filteredCities.length === 50 
                ? 'Mostrando 50 primeiros resultados. Use a busca para refinar.'
                : `${filteredCities.length} cidade(s) encontrada(s)`}
            </div>
          )}
        </div>
      )}

      {/* Overlay para fechar ao clicar fora */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CitySelect;